import { useState } from 'react';
import { Shield, Eye, EyeOff, User, Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PatreonLoginModal = ({ open, onOpenChange, onLoginSuccess }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/patreon/login`, {
        username: formData.username,
        password: formData.password
      });
      
      // Store the Patreon token
      localStorage.setItem('patreon_token', response.data.access_token);
      localStorage.setItem('auth_type', 'patreon');
      
      toast.success('Welcome, Patreon member!');
      
      // Reset form and close
      setFormData({ username: '', password: '' });
      onOpenChange(false);
      
      // Notify parent
      if (onLoginSuccess) {
        onLoginSuccess(response.data);
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid credentials';
      setError(message);
      if (message.includes('disabled')) {
        toast.error('Patreon access is currently disabled');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md mx-4" data-testid="patreon-login-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <DialogTitle className="font-heading text-xl font-bold text-zinc-100 tracking-tight">
                Patreon Member Access
              </DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm">
                Enter your shared Patreon credentials
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="patreon-auth-error">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="username" className="text-zinc-300 text-sm">Username</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="Patreon username"
                className="pl-10 bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-600"
                required
                data-testid="patreon-username-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-zinc-300 text-sm">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                placeholder="••••••••"
                className="pl-10 pr-10 bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-600"
                required
                data-testid="patreon-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2 rounded-lg"
            data-testid="patreon-login-submit"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Signing In...
              </span>
            ) : (
              'Access Network'
            )}
          </Button>

          <p className="text-center text-zinc-500 text-xs pt-2">
            Credentials provided by your Patreon membership
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PatreonLoginModal;
